import React, { useMemo } from "react";
import { useAtomValue } from "jotai";

import { PageLayout } from "components/PageLayout";
import { BoldText, MediumText, RegularText } from "components/Text";
import { Input } from "components/Input";
import { Checkbox } from "components/Checkbox";
import { Select } from "components/Select";
import { Divider, VerticalSpace } from "components/Spacing";

import { settingsAtom } from "atoms/settingsAtom";
import { usePriceLists, useStores, useMeasureUnits } from "shared-apis";

import styles from "./styles.module.scss";

const LABEL = 200;
const INPUT_W = 280;

const yesNo = (value) => Number(value) === 1;

/** Экран настроек — пока просмотр текущих значений из API */
export const SettingsPage = () => {
  const settings = useAtomValue(settingsAtom);
  const priceListsQuery = usePriceLists();
  const storesQuery = useStores();
  const measuresQuery = useMeasureUnits();

  const priceListOptions = useMemo(
    () =>
      (priceListsQuery.data?.items ?? []).map((p) => ({
        value: String(p.pricelistid),
        label: p.name,
      })),
    [priceListsQuery.data]
  );

  const storeOptions = useMemo(
    () =>
      (storesQuery.data?.items ?? []).map((s) => ({
        value: String(s.storesid),
        label: s.name,
      })),
    [storesQuery.data]
  );

  const measureOptions = useMemo(
    () =>
      (measuresQuery.data?.items ?? []).map((m) => ({
        value: String(m.measureid),
        label: m.name,
      })),
    [measuresQuery.data]
  );

  const priceListVal =
    priceListOptions.find(
      (o) => String(o.value) === String(settings?.pricelistid ?? "")
    ) || null;
  const storeVal =
    storeOptions.find(
      (o) => String(o.value) === String(settings?.storesid ?? "")
    ) || null;
  const measureVal =
    measureOptions.find(
      (o) => String(o.value) === String(settings?.defaultmeasureid ?? "")
    ) || null;

  const hasSettings = Boolean(settings?.settingsid);

  return (
    <div className={styles.page}>
      <PageLayout
        className={styles.card}
        header={<BoldText>Настройки</BoldText>}
        bodyFill
      >
        {!hasSettings ? (
          <RegularText color="muted" size={14}>
            Настройки ещё не загружены. Обновите страницу или проверьте
            соединение с сервером.
          </RegularText>
        ) : (
          <div className={styles.content}>
            <MediumText size={15}>Основные</MediumText>
            <VerticalSpace size={16} />
            <div className={styles.fields}>
              <Input
                label="Версия"
                labelWidth={LABEL}
                value={settings.version || "—"}
                disabled
                inputWidth={INPUT_W}
              />
              <Input
                label="Валюта"
                labelWidth={LABEL}
                value={settings.currency || "₽"}
                disabled
                inputWidth={INPUT_W}
              />
              <Select
                label="Прайс по умолчанию"
                labelWidth={LABEL}
                options={priceListOptions}
                value={priceListVal}
                isDisabled
                width={INPUT_W}
                placeholder="Не задан"
              />
              <Select
                label="Склад по умолчанию"
                labelWidth={LABEL}
                options={storeOptions}
                value={storeVal}
                isDisabled
                width={INPUT_W}
                placeholder="Не задан"
              />
              <Select
                label="Ед. изм. по умолчанию"
                labelWidth={LABEL}
                options={measureOptions}
                value={measureVal}
                isDisabled
                width={INPUT_W}
                placeholder="Не задана"
              />
            </div>

            <VerticalSpace size={24} />
            <Divider />
            <VerticalSpace size={24} />

            <MediumText size={15}>Штрихкоды и букеты</MediumText>
            <VerticalSpace size={16} />
            <div className={styles.fields}>
              <Input
                label="Префикс ШК"
                labelWidth={LABEL}
                value={settings.barcodeprefix || "—"}
                disabled
                inputWidth={INPUT_W}
              />
              <Input
                label="Название букета"
                labelWidth={LABEL}
                value={settings.bouquetname || "—"}
                disabled
                inputWidth={INPUT_W}
              />
              <Input
                label="Номер букета"
                labelWidth={LABEL}
                value={String(settings.bouquetnumber ?? "—")}
                disabled
                inputWidth={INPUT_W}
              />
            </div>

            <VerticalSpace size={24} />
            <Divider />
            <VerticalSpace size={24} />

            <MediumText size={15}>Опции</MediumText>
            <VerticalSpace size={16} />
            <div className={styles.checks}>
              <Checkbox
                label="Использовать скидки"
                checked={yesNo(settings.usediscount)}
                disabled
              />
              <Checkbox
                label="Быстрая продажа"
                checked={yesNo(settings.easysale)}
                disabled
              />
              <Checkbox
                label="Контроль штрихкода"
                checked={yesNo(settings.controlbarcode)}
                disabled
              />
              <Checkbox
                label="История цен"
                checked={yesNo(settings.usepricehistory)}
                disabled
              />
              <Checkbox
                label="Показывать холодильник"
                checked={yesNo(settings.showrefrigerator)}
                disabled
              />
            </div>

            <VerticalSpace size={28} />
            <RegularText size={13} color="muted">
              Редактирование настроек появится позже. Сейчас отображаются
              значения с сервера.
            </RegularText>
          </div>
        )}
      </PageLayout>
    </div>
  );
};
